import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext/CookieConsentContext';

const GA_MEASUREMENT_ID = 'G-CFGZ2PFLM4';

/**
 * Initialize Google Consent Mode v2 defaults
 * Must be called before GA script loads
 */
const initConsentDefaults = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  // Set default consent to denied for all categories
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
};

/**
 * Update Google Consent Mode based on user preferences
 */
const updateGoogleConsent = (analyticsAllowed, marketingAllowed) => {
  if (!window.gtag) return;

  window.gtag('consent', 'update', {
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
    ad_storage: marketingAllowed ? 'granted' : 'denied',
    ad_user_data: marketingAllowed ? 'granted' : 'denied',
    ad_personalization: marketingAllowed ? 'granted' : 'denied',
  });
};

/**
 * Hook to initialize Google Analytics and track page views
 * Implements Google Consent Mode v2 for GDPR compliance
 */
export const useGoogleAnalytics = () => {
  const { analyticsAllowed, marketingAllowed, isLoaded, hasConsent } = useCookieConsent();
  const location = useLocation();
  const isInitialized = useRef(false);
  const lastTrackedPath = useRef(null);
  const consentDefaultsSet = useRef(false);

  // Set consent defaults before anything else
  useEffect(() => {
    if (consentDefaultsSet.current) return;

    initConsentDefaults();
    consentDefaultsSet.current = true;
  }, []);

  // Update consent mode when user makes a choice
  useEffect(() => {
    if (!isLoaded || !hasConsent) return;

    updateGoogleConsent(analyticsAllowed, marketingAllowed);
  }, [isLoaded, hasConsent, analyticsAllowed, marketingAllowed]);

  // Initialize GA script when consent is given for analytics
  useEffect(() => {
    if (!isLoaded || !analyticsAllowed || isInitialized.current) {
      return;
    }

    // Check if GA script is already loaded
    const existingScript = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js"]`
    );
    if (existingScript) {
      isInitialized.current = true;
      return;
    }

    // Load GA script dynamically
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });

    isInitialized.current = true;
  }, [isLoaded, analyticsAllowed]);

  // Track page views on route change
  useEffect(() => {
    if (!isInitialized.current || !analyticsAllowed) {
      return;
    }

    if (lastTrackedPath.current === location.pathname) {
      return;
    }

    lastTrackedPath.current = location.pathname;
    trackPageView(location.pathname);
  }, [location.pathname, analyticsAllowed]);

  return {
    isInitialized: isInitialized.current,
    trackEvent,
    trackPageView,
  };
};

/**
 * Track a page view
 * @param {string} path - Page path
 */
const trackPageView = (path) => {
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: document.title,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - Event name
 * @param {Object} params - Event parameters
 */
const trackEvent = (eventName, params = {}) => {
  if (!window.gtag) return;

  window.gtag('event', eventName, params);
};

export { trackEvent, trackPageView };
